import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { User, CreateUserRequest, UpdateUserRequest, UserRole } from '../types';
import authService from '../services/authService';
import { usePageTitle } from '../hooks/usePageTitle';
import { PageHeader, Badge, Avatar, Spinner, ModalOverlay } from '../components/ui';

const Users: React.FC = () => {
  usePageTitle('User Management');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const [deletingUser, setDeletingUser] = useState<User | null>(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    password: '',
    role: UserRole.USER as UserRole,
    active: true
  });

  const modalRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleModalKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      resetForm();
      return;
    }
    if (e.key === 'Tab' && modalRef.current) {
      const focusable = modalRef.current.querySelectorAll<HTMLElement>(
        'input, select, button, [tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  useEffect(() => {
    if (showCreateForm && modalRef.current) {
      const firstInput = modalRef.current.querySelector<HTMLElement>('input');
      firstInput?.focus();
    }
    if (!showCreateForm && triggerRef.current) {
      triggerRef.current.focus();
    }
  }, [showCreateForm]);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const userList = await authService.getAllUsers();
      setUsers(userList);
    } catch (error) {
      toast.error('Couldn\u2019t load users \u2014 check your connection and try again');
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingUser) {
        const updateData: UpdateUserRequest = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          username: formData.username,
          role: formData.role,
          active: formData.active,
          ...(formData.password && { password: formData.password })
        };

        await authService.updateUser(editingUser.id, updateData);
        toast.success('Changes saved');
        setEditingUser(null);
      } else {
        const createData: CreateUserRequest = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          username: formData.username,
          password: formData.password,
          role: formData.role
        };

        await authService.createUser(createData);
        toast.success('User added to your team');
        setShowCreateForm(false);
      }

      setFormData({
        firstName: '',
        lastName: '',
        username: '',
        password: '',
        role: UserRole.USER,
        active: true
      });

      await loadUsers();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save user');
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      username: user.username,
      password: '',
      role: user.role,
      active: user.active
    });
    setShowCreateForm(true);
  };

  const confirmDelete = async () => {
    if (!deletingUser) return;
    try {
      await authService.deleteUser(deletingUser.id);
      toast.success('User removed');
      setDeletingUser(null);
      await loadUsers();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete user');
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      username: '',
      password: '',
      role: UserRole.USER,
      active: true
    });
    setEditingUser(null);
    setShowCreateForm(false);
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-10">
        <div className="mb-8">
          <div className="skeleton h-9 w-24 mb-2" />
          <div className="skeleton h-4 w-40" />
        </div>
        <div className="hidden md:block rounded-xl overflow-hidden" style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}>
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center gap-4 px-6 py-4" style={{ borderBottom: '1px solid var(--surface-border)' }}>
              <div className="skeleton h-8 w-8 rounded-full flex-shrink-0" />
              <div className="skeleton h-4 w-32" />
              <div className="skeleton h-4 w-20 ml-auto" />
              <div className="skeleton h-5 w-14 rounded-full" />
              <div className="skeleton h-5 w-14 rounded-full" />
            </div>
          ))}
        </div>
        <div className="md:hidden space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="rounded-lg p-4" style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}>
              <div className="flex items-center gap-3">
                <div className="skeleton h-10 w-10 rounded-full flex-shrink-0" />
                <div>
                  <div className="skeleton h-4 w-28 mb-1.5" />
                  <div className="skeleton h-3 w-20" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const roleBadge = (role: UserRole) => (
    <Badge variant={role === UserRole.ADMIN ? 'purple' : 'green'}>
      {role === UserRole.ADMIN ? 'Admin' : 'User'}
    </Badge>
  );

  const statusBadge = (active: boolean) => (
    <Badge variant={active ? 'green' : 'red'}>
      {active ? 'Active' : 'Inactive'}
    </Badge>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-10">
      <PageHeader
        title="Users"
        subtitle={`${users.length} ${users.length === 1 ? 'account' : 'accounts'} registered`}
        action={
          <button
            ref={triggerRef}
            onClick={() => setShowCreateForm(true)}
            className="btn-primary bg-brand-600 text-white px-5 py-2.5 rounded-lg hover:bg-brand-700 font-semibold text-sm"
          >
            + New User
          </button>
        }
      />

      {/* Create/Edit Form Modal */}
      {showCreateForm && (
        <ModalOverlay
          role="dialog"
          ariaLabelledBy="user-modal-title"
          onKeyDown={handleModalKeyDown}
          innerRef={modalRef}
        >
          <div className="rounded-lg p-6 w-full max-w-md" style={{ background: 'var(--surface-bg)', color: 'var(--text-primary)' }}>
            <h2 id="user-modal-title" className="text-lg font-semibold mb-4">
              {editingUser ? 'Edit User' : 'New User'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="user-firstName" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>First Name</label>
                  <input
                    id="user-firstName"
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full p-2 border rounded-lg input-themed"
                  />
                </div>
                <div>
                  <label htmlFor="user-lastName" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Last Name</label>
                  <input
                    id="user-lastName"
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full p-2 border rounded-lg input-themed"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="user-username" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Username</label>
                <input
                  id="user-username"
                  type="text"
                  required
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full p-2 border rounded-lg input-themed"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="user-password" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                  Password {editingUser && '(leave empty to keep current)'}
                </label>
                <input
                  id="user-password"
                  type="password"
                  required={!editingUser}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full p-2 border rounded-lg input-themed"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="user-role" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Role</label>
                <select
                  id="user-role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                  className="w-full p-2 border rounded-lg input-themed"
                >
                  <option value={UserRole.USER}>User</option>
                  <option value={UserRole.ADMIN}>Administrator</option>
                </select>
              </div>

              {editingUser && (
                <div className="mb-4">
                  <label htmlFor="user-active" className="flex items-center" style={{ color: 'var(--text-primary)' }}>
                    <input
                      id="user-active"
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      className="mr-2"
                    />
                    Active
                  </label>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 rounded-lg transition-colors"
                  style={{ color: 'var(--text-secondary)', background: 'var(--table-header-bg)' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700"
                >
                  {editingUser ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </ModalOverlay>
      )}

      {/* Delete Confirmation Dialog */}
      {deletingUser && (
        <ModalOverlay
          role="alertdialog"
          ariaLabelledBy="delete-dialog-title"
          ariaDescribedBy="delete-dialog-desc"
          onKeyDown={(e) => { if (e.key === 'Escape') setDeletingUser(null); }}
        >
          <div className="rounded-lg p-6 w-full max-w-sm" style={{ background: 'var(--surface-bg)' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--badge-red-bg)' }}>
                <svg className="h-5 w-5" style={{ color: 'var(--badge-red-text)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
              </div>
              <div>
                <h2 id="delete-dialog-title" className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Delete User
                </h2>
                <p id="delete-dialog-desc" className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                  Are you sure you want to delete <strong>{deletingUser.firstName} {deletingUser.lastName}</strong>? This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeletingUser(null)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{ color: 'var(--text-secondary)', background: 'var(--table-header-bg)' }}
                autoFocus
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* Desktop: Table view */}
      <div className="hidden md:block rounded-xl shadow overflow-x-auto animate-fade-up stagger-2" style={{ background: 'var(--surface-bg)', boxShadow: 'var(--surface-shadow)', border: '1px solid var(--surface-border)' }}>
        <table className="min-w-full" style={{ borderColor: 'var(--table-border)' }}>
          <thead style={{ background: 'var(--table-header-bg)' }}>
            <tr>
              {['Name', 'Username', 'Role', 'Status', 'Created'].map((h) => (
                <th key={h} className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--table-border)' }}>
                  {h}
                </th>
              ))}
              <th className="px-6 py-3.5 text-right text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--table-border)' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="table-row-interactive" style={{ borderBottom: '1px solid var(--table-border)' }}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <Avatar firstName={user.firstName} lastName={user.lastName} size="sm" />
                    <div className="ml-3 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      {user.firstName} {user.lastName}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-primary)' }}>
                  {user.username}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{roleBadge(user.role)}</td>
                <td className="px-6 py-4 whitespace-nowrap">{statusBadge(user.active)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-muted)' }}>
                  {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US') : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => handleEdit(user)}
                    className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300 px-2 py-1.5 rounded transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setDeletingUser(user)}
                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 px-2 py-1.5 rounded ml-1 transition-colors"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {users.length === 0 && (
          <div className="text-center py-16 px-6 animate-fade-up">
            <div
              className="mx-auto h-14 w-14 rounded-2xl flex items-center justify-center mb-5 animate-float"
              style={{ background: 'var(--accent-warm-bg)', border: '1px solid var(--accent-warm-border)' }}
            >
              <svg className="h-6 w-6" style={{ color: 'var(--accent-warm)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
              </svg>
            </div>
            <p className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Your team starts here</p>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Add your first user to begin managing access.</p>
          </div>
        )}
      </div>

      {/* Mobile: Card view */}
      <div className="md:hidden space-y-3">
        {users.map((user) => (
          <div
            key={user.id}
            className="rounded-lg p-4"
            style={{ background: 'var(--surface-bg)', boxShadow: 'var(--surface-shadow)' }}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3 min-w-0">
                <Avatar firstName={user.firstName} lastName={user.lastName} size="md" />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                    {user.firstName} {user.lastName}
                  </div>
                  <div className="text-sm truncate" style={{ color: 'var(--text-muted)' }}>
                    {user.username}
                  </div>
                </div>
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                {roleBadge(user.role)}
                {statusBadge(user.active)}
              </div>
            </div>
            <div className="flex items-center justify-between pt-3" style={{ borderTop: '1px solid var(--table-border)' }}>
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US') : '-'}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(user)}
                  className="text-brand-600 dark:text-brand-400 text-sm font-medium px-3 py-2 rounded-md transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                  style={{ background: 'var(--table-header-bg)' }}
                >
                  Edit
                </button>
                <button
                  onClick={() => setDeletingUser(user)}
                  className="text-red-600 dark:text-red-400 text-sm font-medium px-3 py-2 rounded-md transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                  style={{ background: 'var(--table-header-bg)' }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}

        {users.length === 0 && (
          <div className="text-center py-16 px-6 animate-fade-up">
            <div
              className="mx-auto h-14 w-14 rounded-2xl flex items-center justify-center mb-5 animate-float"
              style={{ background: 'var(--accent-warm-bg)', border: '1px solid var(--accent-warm-border)' }}
            >
              <svg className="h-6 w-6" style={{ color: 'var(--accent-warm)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
              </svg>
            </div>
            <p className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Your team starts here</p>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Add your first user to begin managing access.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Users;
